import type { SuggestionStatus, SuggestionsV2 } from '@/lib/suggestionsV2';

const inMemory = new Map<string, Record<string, SuggestionStatus>>();
const inMemoryMailDedupe = new Set<string>();

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const getStorageKey = (structureId: string) => `ss:${structureId}`;

const readFromSessionStorage = (structureId: string): Record<string, SuggestionStatus> => {
  const storage = getSessionStorage();
  if (!storage) return {};
  try {
    const raw = storage.getItem(getStorageKey(structureId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, SuggestionStatus> = {};
    Object.entries(parsed).forEach(([key, value]) => {
      if (value === 'todo' || value === 'in_progress' || value === 'done' || value === 'rejected') {
        out[key] = value;
      }
    });
    return out;
  } catch {
    return {};
  }
};

const writeToSessionStorage = (structureId: string, value: Record<string, SuggestionStatus>) => {
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(getStorageKey(structureId), JSON.stringify(value));
  } catch {
    // ignore storage write failures
  }
};

const getStructureMap = (structureId: string): Record<string, SuggestionStatus> => {
  if (!structureId) return {};
  const cached = inMemory.get(structureId);
  if (cached) return cached;
  const loaded = readFromSessionStorage(structureId);
  inMemory.set(structureId, loaded);
  return loaded;
};

export const getSessionStatus = (structureId: string, suggestionId: string): SuggestionStatus | null => {
  if (!structureId || !suggestionId) return null;
  const map = getStructureMap(structureId);
  return map[suggestionId] ?? null;
};

export const setSessionStatus = (structureId: string, suggestionId: string, status: SuggestionStatus): void => {
  if (!structureId || !suggestionId) return;
  const map = { ...getStructureMap(structureId), [suggestionId]: status };
  inMemory.set(structureId, map);
  writeToSessionStorage(structureId, map);
};

export const setSessionStatuses = (structureId: string, statusByItemId: Record<string, SuggestionStatus>): void => {
  if (!structureId) return;
  const map = { ...getStructureMap(structureId), ...statusByItemId };
  inMemory.set(structureId, map);
  writeToSessionStorage(structureId, map);
};

export const getAllSessionStatusesForStructure = (structureId: string): Record<string, SuggestionStatus> => {
  if (!structureId) return {};
  const map = getStructureMap(structureId);
  return { ...map };
};

export const applySessionStatusesToSuggestions = (suggestions: SuggestionsV2 | null): SuggestionsV2 | null => {
  if (!suggestions) return suggestions;
  const overrides = getAllSessionStatusesForStructure(suggestions.structureId);
  if (!overrides || Object.keys(overrides).length === 0) return suggestions;

  const categories = suggestions.categories.map((category) => ({
    ...category,
    items: category.items.map((item) => {
      const override = overrides[item.id];
      return override ? { ...item, status: override } : item;
    }),
  }));

  return { ...suggestions, categories };
};

export const isMailDedupeHit = (dedupeKey: string): boolean => {
  if (!dedupeKey) return false;
  if (inMemoryMailDedupe.has(dedupeKey)) return true;
  const storage = getSessionStorage();
  if (!storage) return false;
  try {
    return storage.getItem(`mail_dedupe:${dedupeKey}`) === '1';
  } catch {
    return false;
  }
};

export const markMailDedupe = (dedupeKey: string): void => {
  if (!dedupeKey) return;
  inMemoryMailDedupe.add(dedupeKey);
  const storage = getSessionStorage();
  if (!storage) return;
  try {
    storage.setItem(`mail_dedupe:${dedupeKey}`, '1');
  } catch {
    // ignore storage write failures
  }
};
